export enum JsonFieldType {
  string = "string",
  number = "number",
  integer = "integer",
  boolean = "boolean",
  object = "object",
  array = "array",
}

export const NULL = "null"
export type NullableJsonFieldType<T extends JsonFieldType> =
  | T
  | [T, typeof NULL]
  | [typeof NULL, T]

type JsonBase<T extends JsonFieldType> = {
  type: NullableJsonFieldType<T>
  description?: string
}

export const JSON_STRING_FORMATS = [
  "date-time",
  "date",
  "time",
  "duration",
  "email",
  "hostname",
  "ipv4",
  "ipv6",
  "uuid",
] as const

export type JsonStringFormat = (typeof JSON_STRING_FORMATS)[number]

export type JsonString = JsonBase<JsonFieldType.string> & {
  minLength?: number
  maxLength?: number
  format?: JsonStringFormat
  enum?: string[]
}

export type JsonNumber = JsonBase<JsonFieldType.number> & {
  minimum?: number
  maximum?: number
  enum?: number[]
}

export type JsonInteger = JsonBase<JsonFieldType.integer> & {
  minimum?: number
  maximum?: number
  enum?: number[]
}

export type JsonBoolean = JsonBase<JsonFieldType.boolean>

export type JsonObject = JsonBase<JsonFieldType.object> & {
  properties: Record<string, JsonField>
  required: string[]
  additionalProperties: false
}

export type JsonArray = JsonBase<JsonFieldType.array> & {
  items: JsonField
  minItems?: number
  maxItems?: number
}

export type JsonField =
  | JsonString
  | JsonNumber
  | JsonInteger
  | JsonBoolean
  | JsonObject
  | JsonArray

/** Keywords every field accepts, whatever its type. */
export const COMMON_KEYWORDS = ["type", "description"] as const

/**
 * The keywords each type accepts on top of {@link COMMON_KEYWORDS}. Anything else is rejected:
 * LLM providers refuse schemas carrying keywords their structured-output mode does not support,
 * so accepting one here would only surface as a request failure later.
 *
 * Shared with `jsonFieldMetaSchema`, which must document exactly these keywords.
 */
export const TYPE_KEYWORDS = {
  [JsonFieldType.string]: ["minLength", "maxLength", "format", "enum"],
  [JsonFieldType.number]: ["minimum", "maximum", "enum"],
  [JsonFieldType.integer]: ["minimum", "maximum", "enum"],
  [JsonFieldType.boolean]: [],
  [JsonFieldType.object]: ["properties", "required", "additionalProperties"],
  [JsonFieldType.array]: ["items", "minItems", "maxItems"],
} as const satisfies Record<JsonFieldType, readonly string[]>

/** One-line descriptions for the editor's property-name completions. */
export const KEYWORD_DESCRIPTIONS: Record<string, string> = {
  type: "The field type",
  description: "Optional description of the field",
  minLength: "Minimum string length",
  maxLength: "Maximum string length",
  format: "Semantic format hint for the string value",
  enum: "Allowed values for the field",
  minimum: "Minimum value",
  maximum: "Maximum value",
  properties: "Object properties mapping keys to JsonFields",
  required: "Required property names",
  additionalProperties: "Must be false",
  items: "Schema for array items",
  minItems: "Minimum array length",
  maxItems: "Maximum array length",
}

export function defaultField(type: JsonFieldType): JsonField {
  switch (type) {
    case JsonFieldType.string:
      return { type }
    case JsonFieldType.number:
      return { type }
    case JsonFieldType.integer:
      return { type }
    case JsonFieldType.boolean:
      return { type }
    case JsonFieldType.object:
      return {
        type,
        properties: {},
        required: [],
        additionalProperties: false,
      }
    case JsonFieldType.array:
      return {
        type,
        items: { type: JsonFieldType.string },
      }
  }
}
